import { languageForPath, type LanguageId } from "@ide/shared";

/**
 * Executar e depurar o arquivo aberto, reconhecendo a linguagem (como o "Run Python File" do
 * VS Code). Só aparece para linguagens que rodam sozinhas (Python, Java, C, Go…); HTML ganha o
 * Live Server, e CSS, JSON, Markdown e o JavaScript de página não têm "Executar".
 *
 *  - local: terminal da máquina da pessoa (PowerShell no Windows: sem `&&`, encadeia pelo cmd);
 *  - sandbox: Linux na nuvem.
 */
export type RunTarget = "local" | "sandbox";
export type Platform = "win32" | "darwin" | "linux";

export interface FileRunner {
  language: LanguageId;
  /** Nome curto para os menus ("Python", "Java"…). */
  label: string;
  /** Dá para depurar com pontos de parada no terminal (pdb, gdb). */
  debuggable: boolean;
}

const RUNNERS: Partial<Record<LanguageId, Omit<FileRunner, "language">>> = {
  python: { label: "Python", debuggable: true },
  java: { label: "Java", debuggable: false },
  kotlin: { label: "Kotlin", debuggable: false },
  c: { label: "C", debuggable: true },
  cpp: { label: "C++", debuggable: true },
  csharp: { label: "C#", debuggable: false },
  go: { label: "Go", debuggable: false },
  rust: { label: "Rust", debuggable: false },
  php: { label: "PHP", debuggable: false },
  ruby: { label: "Ruby", debuggable: false },
  shell: { label: "Shell", debuggable: false },
  swift: { label: "Swift", debuggable: false },
  typescript: { label: "TypeScript", debuggable: false },
  javascript: { label: "Node.js", debuggable: false },
};

/** O que o botão Executar oferece para o arquivo aberto. */
export type RunOffer =
  { kind: "file"; runner: FileRunner } | { kind: "live" } | { kind: "project" } | { kind: "none" };

/**
 * Decide o Executar pelo arquivo: linguagens executáveis rodam o arquivo; HTML vira Live Server;
 * JavaScript/TypeScript só rodam como projeto Node (com package.json) — o .js de uma página roda
 * no navegador, não no terminal; o resto (CSS, JSON, Markdown…) não tem Executar.
 */
export function runOfferFor(path: string, projectPaths: Iterable<string>): RunOffer {
  const language = languageForPath(path);
  if (language === "html") return { kind: "live" };
  const paths = [...projectPaths];
  const node = paths.includes("package.json");
  if (language === "javascript" || language === "typescript" || language === "jsx" || language === "tsx") {
    // Script solto num projeto Node (scripts/, bin/, server.js): roda o arquivo; senão, o projeto.
    if (!node) return { kind: "none" };
    return { kind: "project" };
  }
  const runner = RUNNERS[language];
  if (runner) return { kind: "file", runner: { language, ...runner } };
  // Arquivos de configuração do projeto (Dockerfile, compose, requirements…): Executar o projeto.
  if (
    language === "dockerfile" ||
    /(^|\/)(requirements\.txt|pyproject\.toml|pom\.xml|go\.mod|Cargo\.toml)$/.test(path)
  )
    return { kind: "project" };
  return { kind: "none" };
}

const q = (p: string) => (/[\s"'&;()$`]/.test(p) ? `"${p.replace(/"/g, '\\"')}"` : p);
const base = (p: string) => p.split("/").pop()!;
const stem = (p: string) => base(p).replace(/\.[^.]+$/, "");

/** Encadeia comandos: `a && b` (bash/pwsh 7) ou pelo cmd no PowerShell do Windows. */
function chain(parts: string[], target: RunTarget, platform: Platform): string {
  // Aspas simples: o PowerShell repassa o texto ao cmd sem interpretar.
  if (target === "local" && platform === "win32") return `cmd /c '${parts.join(" && ").replace(/'/g, "")}'`;
  return parts.join(" && ");
}

/** Comando que roda o arquivo (caminho relativo à raiz do projeto). */
export function fileRunCommand(path: string, target: RunTarget, platform: Platform = "linux"): string | null {
  const language = languageForPath(path);
  const win = target === "local" && platform === "win32";
  const py = win ? "python" : "python3";
  const out = win ? `.kompil-run\\${stem(path)}.exe` : `/tmp/kompil-run-${stem(path)}`;
  const mk = win ? "if not exist .kompil-run mkdir .kompil-run" : null;
  switch (language) {
    case "python":
      return `${py} ${q(path)}`;
    case "java":
      // Java 11+: roda um arquivo .java direto, sem compilar à parte.
      return `java ${q(path)}`;
    case "kotlin":
      return chain(
        [
          `kotlinc ${q(path)} -include-runtime -d ${win ? `${stem(path)}.jar` : `/tmp/${stem(path)}.jar`}`,
          `java -jar ${win ? `${stem(path)}.jar` : `/tmp/${stem(path)}.jar`}`,
        ],
        target,
        platform,
      );
    case "c":
      return chain([...(mk ? [mk] : []), `gcc -g ${q(path)} -o ${out} -lm`, out], target, platform);
    case "cpp":
      return chain([...(mk ? [mk] : []), `g++ -g -std=c++17 ${q(path)} -o ${out}`, out], target, platform);
    case "csharp":
      return "dotnet run";
    case "go":
      return `go run ${q(path)}`;
    case "rust":
      return chain([...(mk ? [mk] : []), `rustc ${q(path)} -o ${out}`, out], target, platform);
    case "php":
      return `php ${q(path)}`;
    case "ruby":
      return `ruby ${q(path)}`;
    case "shell":
      return `bash ${q(path)}`;
    case "swift":
      return `swift ${q(path)}`;
    case "typescript":
      return `npx --yes tsx ${q(path)}`;
    case "javascript":
      return `node ${q(path)}`;
    default:
      return null;
  }
}

/**
 * Depurar no terminal com os pontos de parada do arquivo: Python pelo pdb (para em cada ponto;
 * `n` próxima linha, `p var` mostra, `c` continua) e C/C++ pelo gdb. Null: sem depurador.
 */
export function fileDebugCommand(
  path: string,
  breakpoints: number[],
  target: RunTarget,
  platform: Platform = "linux",
): string | null {
  const language = languageForPath(path);
  const win = target === "local" && platform === "win32";
  const lines = [...new Set(breakpoints)].sort((a, b) => a - b);
  if (language === "python") {
    const py = win ? "python" : "python3";
    // Caminho a partir da raiz do projeto (a pasta onde o terminal roda): o pdb acha o arquivo.
    const stops = lines.map((l) => `-c ${q(`b ${path}:${l}`)}`).join(" ");
    // Sem pontos, para na primeira linha (como o "Step Into" do VS Code); com pontos, segue até eles.
    return `${py} -m pdb ${stops}${lines.length ? " -c c" : ""} ${q(path)}`.replace(/\s+/g, " ");
  }
  if (language === "c" || language === "cpp") {
    const out = win ? `.kompil-run\\${stem(path)}.exe` : `/tmp/kompil-run-${stem(path)}`;
    const compiler =
      language === "c" ? `gcc -g -O0 ${q(path)} -o ${out} -lm` : `g++ -g -O0 -std=c++17 ${q(path)} -o ${out}`;
    const stops = lines.map((l) => `-ex ${q(`break ${base(path)}:${l}`)}`).join(" ");
    const gdb = `gdb -q ${stops} -ex run ${out}`.replace(/\s+/g, " ");
    return chain(
      [...(win ? ["if not exist .kompil-run mkdir .kompil-run"] : []), compiler, gdb],
      target,
      platform,
    );
  }
  return null;
}
